import {useEffect, useRef} from 'react';
import ReactGA from 'react-ga4';
import {useLocation} from 'react-router';
import {sanitizePagePath} from './sanitizePagePath';

export default function PageViewTracker() {
  const location = useLocation();
  const previousPathRef = useRef<string | null>(null);

  useEffect(() => {
    const rawPath = location.pathname + location.search;
    const currentPath = sanitizePagePath(rawPath);

    // Deduplicated on the RAW path, not the sanitised one. Two stations both
    // sanitise to `/app/stations/:id`, and the map's station panel navigates
    // straight from one to the other — comparing sanitised paths would drop
    // the second pageview entirely and undercount the very metric the
    // collapse exists to make answerable.
    if (previousPathRef.current === rawPath) {
      return;
    }
    previousPathRef.current = rawPath;

    // `set` before `send`, and both carry the sanitised values.
    //
    // Sanitising `page_path` alone does not stop the leak: gtag derives GA4's
    // page dimensions from `page_location`, which it auto-collects from
    // `document.location.href` and attaches to EVERY event — `login`,
    // `view_premium_page`, `article_feedback`, all of them. `set` overrides
    // that default for subsequent events.
    //
    // `page_title` is sanitised for the same reason: the station screens put
    // the user's own station name into `document.title`
    // (SmartMixinHelmet + Station.getStationName), which is free text and
    // frequently a home address — strictly more identifying than the numeric
    // id this file just removed, and it would re-split the rows the collapse
    // merges. The path is the title now.
    const pageLocation = `${window.location.origin}${currentPath}`;
    ReactGA.set({
      page_location: pageLocation,
      page_path: currentPath,
      page_title: currentPath,
    });
    ReactGA.send({
      hitType: 'pageview',
      page: currentPath,
      title: currentPath,
      location: pageLocation,
    });
  }, [location]);

  return null;
}
